(() => {
  'use strict';
  if (customElements.get('gibous-size-recommend')) return;

  const STORAGE_KEY = 'gibous_size_profile_v1';
  const FIT_LABELS = { snug: 'ぴったり', standard: '標準', relaxed: 'ゆったり' };

  class GibousSizeRecommend extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.parentPdp = this.closest('gibous-pdp');
      this.dialog = this.querySelector('[data-size-rec-dialog]');
      this.trigger = this.querySelector('[data-size-rec-open]');
      this.inputView = this.querySelector('[data-size-rec-input-view]');
      this.resultView = this.querySelector('[data-size-rec-result-view]');
      this.heightInput = this.querySelector('[data-size-rec-height]');
      this.weightInput = this.querySelector('[data-size-rec-weight]');
      this.waistInput = this.querySelector('[data-size-rec-waist]');
      this.selectButton = this.querySelector('[data-size-rec-select]');
      try {
        this.rules = this.readJson('[data-size-rec-rules]');
        this.product = this.readJson('[data-size-rec-product]');
        this.hydrateSizeTableRules();
        this.syncInputRanges();
        const sizeValues = this.product.variants.map((variant) => variant.options?.[this.rules.size_option_name]).filter(Boolean);
        const validation = window.GibousSizeRecommendCore?.validateRules(this.rules, sizeValues);
        if (!validation?.valid || this.product.version !== this.rules.version) throw new Error(validation?.errors?.join(' ') || 'ルール版が一致しません。');
        this.bind();
        this.removeAttribute('hidden');
        this.track('gibous_size_recommend_impression');
      } catch (error) {
        this.track('gibous_size_recommend_error', { result_type: 'configuration' });
        console.warn('gibous size recommendation disabled:', error);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      document.body.classList.remove('gibous-size-rec-modal-open');
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) throw new Error('必要なJSONがありません。');
      return JSON.parse(node.textContent);
    }

    hydrateSizeTableRules() {
      if (this.rules.mode !== 'bottoms' || this.rules.waist_source !== 'size_table') return;
      const table = this.parentPdp?.querySelector('.gibous-pdp__table-scroll table');
      if (!table) return;
      const header = [...table.querySelectorAll('tr')].find((row) => [...row.cells].some((cell) => /ウエスト|waist/i.test(cell.textContent)));
      if (!header) return;
      const headers = [...header.cells].map((cell) => cell.textContent.replace(/\s+/g, ''));
      const waistIndex = headers.findIndex((value) => /ウエスト|waist/i.test(value));
      for (const row of table.querySelectorAll('[data-size-row]')) {
        const match = row.cells[waistIndex]?.textContent.match(/(\d+(?:\.\d+)?)\s*[~〜～-]\s*(\d+(?:\.\d+)?)/);
        const rule = this.rules.sizes.find((size) => size.variant_value === row.dataset.sizeRow);
        if (match && rule) { rule.waist_min = Number(match[1]); rule.waist_max = Number(match[2]); }
      }
    }

    syncInputRanges() {
      const ranges = this.rules.input_ranges || {};
      const inputs = [
        [this.heightInput, ranges.height_min, ranges.height_max],
        [this.weightInput, ranges.weight_min, ranges.weight_max],
        [this.waistInput, ranges.waist_min, ranges.waist_max],
      ];
      for (const [input, min, max] of inputs) {
        if (!input) continue;
        if (Number.isFinite(min)) input.min = String(min);
        if (Number.isFinite(max)) input.max = String(max);
      }
    }

    sizeLabel(value) {
      return this.rules.mode === 'length_choice' ? value : `${value}サイズ`;
    }

    listen(target, type, callback, options = {}) {
      target.addEventListener(type, callback, { ...options, signal: this.controller.signal });
    }

    bind() {
      this.listen(this.trigger, 'click', () => this.open());
      this.querySelectorAll('[data-size-rec-close]').forEach((button) => this.listen(button, 'click', () => this.close()));
      this.listen(this.dialog, 'close', () => {
        document.body.classList.remove('gibous-size-rec-modal-open');
        this.trigger.focus({ preventScroll: true });
      });
      this.listen(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.listen(this.dialog, 'keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input[type="number"]')) {
          event.preventDefault();
          this.submit();
        }
      });
      this.listen(this.querySelector('[data-size-rec-submit]'), 'click', () => this.submit());
      this.querySelectorAll('[data-size-rec-fit]').forEach((input) => this.listen(input, 'change', () => {
        if (input.checked) this.querySelectorAll('[data-size-rec-fit]').forEach((other) => { if (other !== input) other.checked = false; });
      }));
      this.listen(this.querySelector('[data-size-rec-clear]'), 'click', () => this.clearProfile());
      this.listen(this.querySelector('[data-size-rec-change]'), 'click', () => this.showInput(false));
      this.listen(this.selectButton, 'click', () => this.applyRecommendation());
      this.listen(this.querySelector('[data-size-rec-size-table]'), 'click', (event) => this.openSizeTable(event));
      const snapLink = this.querySelector('[data-size-rec-snap-link]');
      this.listen(snapLink, 'click', () => this.track('gibous_size_recommend_snap_click', this.lastEventData()));
      if (this.parentPdp?.form) this.listen(this.parentPdp.form, 'variant:add', () => {
        if (this.usedRecommendation) this.track('gibous_size_recommend_cart_add', { ...this.lastEventData(), selected_size: this.currentSize() });
      });
    }

    open() {
      this.returnFocus = document.activeElement;
      this.showInput(true);
      const profile = this.loadProfile();
      this.heightInput.value = profile?.height || '';
      this.weightInput.value = profile?.weight || '';
      this.waistInput.value = '';
      this.querySelector('[data-size-rec-clear]').hidden = !profile;
      document.body.classList.add('gibous-size-rec-modal-open');
      document.dispatchEvent(new CustomEvent('dialog:before-show', { bubbles: true }));
      this.dialog.showModal();
      requestAnimationFrame(() => (this.heightInput.value ? this.querySelector('[data-size-rec-fit]') : this.heightInput).focus());
      this.track('gibous_size_recommend_start');
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    showInput(resetFit) {
      this.inputView.hidden = false;
      this.resultView.hidden = true;
      if (resetFit) this.querySelectorAll('[data-size-rec-fit]').forEach((input) => { input.checked = false; });
      this.clearErrors();
      this.querySelector('[data-size-rec-waist-wrap]').hidden = this.rules.mode !== 'bottoms';
      this.selectButton.hidden = false;
    }

    clearErrors() {
      this.querySelectorAll('[data-size-rec-error], .gibous-size-rec__error').forEach((node) => { node.hidden = true; node.textContent = ''; });
      for (const input of [this.heightInput, this.weightInput, this.waistInput]) input?.removeAttribute('aria-invalid');
    }

    setError(input, selector, message) {
      const node = this.querySelector(selector);
      node.textContent = message;
      node.hidden = false;
      input?.setAttribute('aria-invalid', 'true');
    }

    validateInput() {
      this.clearErrors();
      const height = Number(this.heightInput.value);
      const weight = Number(this.weightInput.value);
      const waist = this.waistInput.value === '' ? null : Number(this.waistInput.value);
      const preference = this.querySelector('[data-size-rec-fit]:checked')?.value || '';
      let firstInvalid = null;
      const validateNumber = (input, value, min, max, selector, label, optional = false) => {
        if (optional && input.value === '') return;
        let message = '';
        if (input.value === '' || !Number.isFinite(value)) message = `${label}を入力してください。`;
        else if (value < min || value > max) message = `${label}は${min}〜${max}の範囲で入力してください。`;
        if (message) { this.setError(input, selector, message); firstInvalid ||= input; }
      };
      const ranges = this.rules.input_ranges;
      validateNumber(this.heightInput, height, ranges.height_min, ranges.height_max, '[data-size-rec-height-error]', '身長');
      validateNumber(this.weightInput, weight, ranges.weight_min, ranges.weight_max, '[data-size-rec-weight-error]', '体重');
      if (this.rules.mode === 'bottoms') validateNumber(this.waistInput, waist, ranges.waist_min, ranges.waist_max, '[data-size-rec-waist-error]', 'ウエスト', true);
      if (!preference) {
        const node = this.querySelector('[data-size-rec-fit-error]');
        node.textContent = '希望の着用感を選択してください。';
        node.hidden = false;
        firstInvalid ||= this.querySelector('[data-size-rec-fit]');
      }
      firstInvalid?.focus();
      return firstInvalid ? null : { height, weight, waist, preference };
    }

    submit() {
      const input = this.validateInput();
      if (!input) return;
      this.saveProfile(input.height, input.weight);
      let result;
      try { result = window.GibousSizeRecommendCore.recommend(this.rules, input); }
      catch (_) { result = { type: 'error' }; }
      if (result.type === 'error') {
        this.track('gibous_size_recommend_error', { result_type: 'runtime' });
        this.renderDifficult(input, this.rules.fallback_message);
        return;
      }
      if (result.type === 'difficult') {
        this.track('gibous_size_recommend_difficult', { preference: input.preference, mode: this.rules.mode, confidence: '判定困難', result_type: 'difficult' });
        this.renderDifficult(input, result.message || this.rules.fallback_message);
        return;
      }
      this.lastResult = result;
      this.lastInput = input;
      this.renderRecommendation(input, result);
    }

    renderRecommendation(input, result) {
      const primaryVariant = this.variantForSize(result.primary);
      const alternativeVariant = result.alternative ? this.variantForSize(result.alternative) : null;
      this.inputView.hidden = true;
      this.resultView.hidden = false;
      const primaryLabel = this.sizeLabel(result.primary);
      this.querySelector('[data-size-rec-result-size]').textContent = primaryLabel;
      const ruleBasis = result.primaryRule?.notes ? ` ${result.primaryRule.notes}` : '';
      const basisLabel = this.rules.mode === 'length_choice' ? '身長・体重・着丈' : '身長・体重・着用感';
      this.querySelector('[data-size-rec-result-reason]').textContent = `${input.height}cm・${input.weight}kgで${FIT_LABELS[input.preference]}に着たい場合、商品別の${basisLabel}ルールでは${primaryLabel}がおすすめです。${ruleBasis}`;
      const alternative = this.querySelector('[data-size-rec-alternative]');
      alternative.hidden = !result.alternative;
      if (result.alternative) {
        const direction = result.scores.find((item) => item.size.variant_value === result.alternative)?.index > result.scores.find((item) => item.size.variant_value === result.primary)?.index ? 'よりゆったり' : 'よりすっきり';
        alternative.textContent = `${this.sizeLabel(result.alternative)}なら${direction}着用できます${alternativeVariant?.available === false ? '（現在SOLD OUT）' : ''}。`;
        this.track('gibous_size_recommend_alternative', { product_id: this.dataset.productId, product_handle: this.dataset.productHandle, recommended_size: result.primary, alternative_size: result.alternative, preference: input.preference, mode: this.rules.mode, confidence: result.confidence });
      }
      const soldout = this.querySelector('[data-size-rec-soldout]');
      soldout.hidden = primaryVariant?.available !== false;
      soldout.textContent = primaryVariant?.available === false ? `現在${primaryLabel}はSOLD OUTです。在庫を理由に別サイズをおすすめすることはありません。` : '';
      this.querySelector('[data-size-rec-note]').textContent = this.product.note || '';
      this.selectButton.hidden = primaryVariant?.available === false || !primaryVariant;
      this.selectButton.textContent = `${primaryLabel}を選択する`;
      this.renderSnap(input.height);
      this.querySelector('[data-size-rec-live]').textContent = `${primaryLabel}をおすすめします。`;
      const eventData = this.lastEventData();
      this.track('gibous_size_recommend_complete', eventData);
      this.track('gibous_size_recommend_result', eventData);
      requestAnimationFrame(() => this.querySelector('[data-size-rec-result-size]').focus?.());
    }

    renderDifficult(input, message) {
      this.lastInput = input;
      this.lastResult = { type: 'difficult', confidence: 'difficult', primary: null, alternative: null };
      this.inputView.hidden = true;
      this.resultView.hidden = false;
      this.querySelector('[data-size-rec-result-size]').textContent = '判定が難しい商品です';
      this.querySelector('[data-size-rec-result-reason]').textContent = message || '入力情報だけでは正確な判定が難しい商品です。';
      this.querySelector('[data-size-rec-alternative]').hidden = true;
      this.querySelector('[data-size-rec-soldout]').hidden = true;
      this.querySelector('[data-size-rec-note]').textContent = this.product.note || '';
      this.selectButton.hidden = true;
      this.renderSnap(input.height);
      this.querySelector('[data-size-rec-live]').textContent = '正確な判定が難しいため、サイズ表または着用SNAPをご確認ください。';
    }

    renderSnap(height) {
      const box = this.querySelector('[data-size-rec-snap]');
      const snaps = (this.product.snaps || []).filter((snap) => Number.isFinite(Number(snap.model_height)) && snap.url && snap.image);
      if (!snaps.length) { box.hidden = true; return; }
      const snap = snaps.sort((a, b) => Math.abs(Number(a.model_height) - height) - Math.abs(Number(b.model_height) - height))[0];
      const link = this.querySelector('[data-size-rec-snap-link]');
      const image = this.querySelector('[data-size-rec-snap-image]');
      link.href = snap.url;
      image.src = snap.image;
      image.alt = snap.image_alt || `${snap.model_name || 'モデル'}の着用例`;
      this.querySelector('[data-size-rec-snap-profile]').textContent = `${snap.model_name || 'Model'} ${snap.model_height}cm${snap.worn_size ? ` / ${snap.worn_size}着用` : ''}`;
      this.querySelector('[data-size-rec-snap-color]').textContent = snap.worn_color ? `COLOR：${snap.worn_color}` : '';
      box.hidden = false;
    }

    variantForSize(size) {
      const sizeOption = this.rules.size_option_name;
      const currentId = this.parentPdp?.variant?.id || this.parentPdp?.querySelector('[name="id"]')?.value;
      const current = this.product.variants.find((variant) => String(variant.id) === String(currentId));
      const candidates = this.product.variants.filter((variant) => variant.options?.[sizeOption] === size);
      if (!current) return candidates[0] || null;
      const matched = candidates.find((variant) => Object.entries(current.options || {}).every(([name, value]) => name === sizeOption || variant.options?.[name] === value));
      return matched || null;
    }

    applyRecommendation() {
      if (!this.lastResult?.primary) return;
      const variant = this.variantForSize(this.lastResult.primary);
      if (!variant?.available || typeof this.parentPdp?.select !== 'function') return;
      this.parentPdp.select(variant.id, true);
      this.usedRecommendation = true;
      this.track('gibous_size_recommend_select', { ...this.lastEventData(), selected_size: this.lastResult.primary });
      this.close();
    }

    openSizeTable(event) {
      event.preventDefault();
      this.close();
      const details = this.parentPdp?.querySelector('[data-size-details]');
      const table = this.parentPdp?.querySelector('.gibous-pdp__table-scroll');
      if (!details || !table) return;
      details.open = true;
      requestAnimationFrame(() => { table.focus({ preventScroll: true }); table.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); });
    }

    currentSize() {
      const currentId = this.parentPdp?.variant?.id;
      return this.product.variants.find((variant) => String(variant.id) === String(currentId))?.options?.[this.rules.size_option_name] || '';
    }

    lastEventData() {
      return {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        recommended_size: this.lastResult?.primary || '',
        alternative_size: this.lastResult?.alternative || '',
        preference: this.lastInput?.preference || '',
        mode: this.rules.mode,
        confidence: this.lastResult?.confidence === 'high' ? '高' : this.lastResult?.confidence === 'medium' ? '中' : '判定困難',
        result_type: this.lastResult?.type || '',
      };
    }

    track(name, detail = {}) {
      const safe = { product_id: this.dataset.productId, product_handle: this.dataset.productHandle, ...detail };
      document.dispatchEvent(new CustomEvent(name, { detail: safe }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...safe });
    }

    loadProfile() {
      try {
        const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (data?.schemaVersion !== 1 || !Number.isFinite(data.height) || !Number.isFinite(data.weight)) throw new Error('invalid');
        return data;
      } catch (_) {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
        return null;
      }
    }

    saveProfile(height, weight) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ height, weight, schemaVersion: 1, updatedAt: new Date().toISOString() })); } catch (_) {}
      this.querySelector('[data-size-rec-clear]').hidden = false;
    }

    clearProfile() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      this.heightInput.value = '';
      this.weightInput.value = '';
      this.querySelector('[data-size-rec-clear]').hidden = true;
      this.querySelector('[data-size-rec-live]').textContent = '保存した体格情報を削除しました。';
      this.heightInput.focus();
    }
  }

  customElements.define('gibous-size-recommend', GibousSizeRecommend);
})();
